<template>
	<!-- Error codes are meant for debugging. So we may see where the customer came from. -->
	<div style="background-color: #ecf2f9">
		<NotFoundPage
			v-if="!!errorMessage"
			:errorMessage="errorMessage"
			errorTitle="Oops... Something went wrong."
		/>
	</div>

	<!--Main Content-->
	<div
		class="login-page prefilled-page iframe"
		v-if="theme"
	>
		<LoaderComponent
			v-if="isLoading"
			:style="{ color: theme.altTextColor }"
		/>
		<LoginError
			:theme="theme"
			:language="fimenu.language"
			v-else-if="creatingError"
		/>
		<LoginSuccess
			:theme="theme"
			:fimenu="fimenu"
			v-else-if="creatingSuccess"
			:sourceData="getSourceData()"
			:customerApp="customerApp"
		/>
		<main v-if="!isLoading && !creatingError && !creatingSuccess && storeCode">
			<button
				class="language-change"
				@click="languageChange"
			>
				{{ localizationOpposite }} <i class="fas fa-globe"></i>
			</button>
			<!--<button @click="fillData" class="autofill-btn"><p>Auto-Fill</p></button>-->
			<!--<h1 :style="{color: theme.altTextColor}">{{localization.creditApp.creationPage.applicantInfo[fimenu.language]}}</h1>-->
			<div class="form">
				<div class="form-group">
					<InputTextbox
						v-model:saturn="firstName"
						:invalid="v$.$dirty && v$.firstName.$invalid"
						:label="firstNameLabel"
						:showIcon="false"
					/>
					<span
						class="textbox-error-message"
						v-if="v$.$dirty && v$.firstName.required.$invalid"
						>{{ requiredMessage }}</span
					>
					<span
						class="textbox-error-message"
						v-else-if="v$.$dirty && v$.firstName.validName.$invalid"
					>
						{{ nameValidationMessage }}
					</span>
				</div>
				<div class="form-group">
					<InputTextbox
						v-model:saturn="lastName"
						:invalid="v$.$dirty && v$.lastName.$invalid"
						:label="lastNameLabel"
						:showIcon="false"
					/>
					<span
						v-if="v$.$dirty && v$.lastName.required.$invalid"
						class="textbox-error-message"
						>{{ requiredMessage }}</span
					>
					<span
						v-else-if="v$.$dirty && v$.lastName.validName.$invalid"
						class="textbox-error-message"
						>{{ nameValidationMessage }}</span
					>
				</div>
				<div class="form-group">
					<InputTextbox
						type="Email"
						v-model:saturn="emailAddress"
						:invalid="v$.$dirty && v$.emailAddress.$invalid"
						:label="emailAddressLabel"
						:showIcon="false"
					/>
					<span
						v-if="v$.$dirty && v$.emailAddress.required.$invalid"
						class="textbox-error-message"
						>{{ requiredMessage }}</span
					>
					<span
						v-else-if="v$.$dirty && v$.emailAddress.email.$invalid"
						class="textbox-error-message"
					>
						Email format should be:
						<span class="error-emphasis">someone@email.com</span></span
					>
				</div>
				<div class="form-group">
					<InputPhone
						type="tel"
						v-model:saturn="phoneNumber"
						:label="phoneNumberLabel"
						:showIcon="false"
						:invalid="v$.$dirty && v$.phoneNumber.$invalid"
						minlength="10"
					/>
					<span
						v-if="v$.$dirty && v$.phoneNumber.required.$invalid"
						class="textbox-error-message"
						>{{ requiredMessage }}</span
					>
				</div>
				<div class="form-group">
					<InputTextDate
						v-model:saturn="dateOfBirth"
						style="width: 100%"
						disableCalendarPopup
						:label="dateOfBirthLabel"
						:invalid="v$.$dirty && v$.dateOfBirth.$invalid"
						:showIcon="false"
						ref="inputDOBRef"
					/>
					<span
						v-if="v$.$dirty && v$.dateOfBirth.required.$invalid"
						class="textbox-error-message"
					>
						{{ requiredMessage }}
					</span>
					<span
						v-else-if="v$.$dirty && (v$.dateOfBirth.validDate.$invalid || v$.dateOfBirth.minLength.$invalid)"
						class="textbox-error-message"
					>
						{{ dateOfBirthFormatMessage }}
					</span>
					<span
						v-else-if="v$.$dirty && v$.dateOfBirth.validDateOfBirth.$invalid"
						class="textbox-error-message"
					>
						{{ dateOfBirthUnderageMessage }}
					</span>
				</div>
			</div>
			<div class="legal-term-container">
				<div
					class="legal-term"
					@click="handleTermsCheckbox(getRelevantTerm(LEGAL_TERM_TYPES.PRIVACY_NOTICE))"
				>
					<button
						class="terms-checkbox-button"
						:class="`${!isPrivacyPolicyChecked && v$.$dirty ? 'invalid' : ''}`"
					>
						<i
							:class="{
								'far fa-check-square': isPrivacyPolicyChecked,
								'far fa-square': !isPrivacyPolicyChecked,
							}"
						></i>
					</button>
					<span
						>{{ iAcknowledge }}
						<button
							@click.stop="openPrivacyPolicyModal"
							class="underline"
						>
							{{ privacyPolicy }}
						</button>
					</span>
				</div>
				<div
					class="legal-term"
					@click="handleTermsCheckbox(getRelevantTerm(LEGAL_TERM_TYPES.SMS_CONSENT))"
				>
					<button
						class="terms-checkbox-button"
						:class="`${!isSMSConsentChecked && v$.$dirty ? 'invalid' : ''}`"
					>
						<i
							:class="{
								'far fa-check-square': isSMSConsentChecked,
								'far fa-square': !isSMSConsentChecked,
							}"
						></i>
					</button>
					<span>
						{{ iAcknowledge }}
						<a
							@click.stop.prevent="openSMSConsentModal"
							class="underline"
							href="#"
						>
							{{ smsConsentButtonMessage }} </a
						>,
						{{ smsConsentText }}
					</span>
				</div>
			</div>
			<button
				class="button-full"
				id="create-application-button"
				ref="createAppButton"
				:style="{
					backgroundColor: theme.successButtonColor,
					color: theme.successButtonTextColor,
				}"
				@click="sendApplication"
			>
				{{ createApplicationMessage }}
			</button>
		</main>
	</div>
</template>
<script lang="ts">
	export interface ICreditAppCreateApplicationProps {
		fimenu: FIMenu;
	}
</script>
<script setup lang="ts">
	import LoginError from '@/components/customerApp/LoginError.vue';
	import LoginSuccess from '@/components/customerApp/LoginSuccess.vue';
	import NotFoundPage from '@/views/customerPortal/NotFoundPage.vue';
	import ApplicationsSettingsLoader from '@core/classes/Applications/ApplicationsSettingsLoader';
	import { LANGUAGE_DISPLAY, LEGAL_TERM_TYPES } from '@core/classes/Enums';
	import FIMenu from '@core/classes/FIMenu';
	import LOCALIZATION from '@core/classes/Localization';
	import InputTextDate from '@core/components/InputDate.vue';
	import InputPhone from '@core/components/InputPhone.vue';
	import InputTextbox from '@core/components/InputTextbox.vue';
	import LoaderComponent from '@core/components/UI/LoaderComponent.vue';
	import modalLegalTerm from '@core/modals/modalLegalTerm.vue';
	import api from '@core/services/api/_api';
	import { validDate, validDateOfBirth, validName } from '@core/services/custom-validators';
	import $modal from '@core/services/modal';
	import { email, maxLength, minLength, numeric, required } from '@vuelidate/validators';
	import LogRocket from 'logrocket';

	import { DEFAULT_DATE_FORMAT_DISPLAY_WITH_TIME, DEFAULT_TIMEZONE } from '@core/_globals/types/date-settings';
	import { $routerProviderKey } from '@core/_globals/types/providerKeys';
	import { LegalTerms } from '@core/classes/LegalTerms';
	import { StoreFileTypes } from '@core/classes/SharedEnums';
	import util from '@core/services/util';
	import { formatDateToDateObject, getTimeZoneDate } from '@core/services/util.date';
	import { useVuelidate } from '@vuelidate/core';
	import settings from 'settings';
	import { computed, inject, onBeforeMount, onMounted, ref, useTemplateRef } from 'vue';
	import { useRoute } from 'vue-router';

	const { fimenu } = defineProps<ICreditAppCreateApplicationProps>();
	const $route = useRoute();
	const $router = inject($routerProviderKey);

	const createAppButtonRef = useTemplateRef('createAppButton');
	const dateOfBirthRef = useTemplateRef('inputDOBRef');

	const customerApp = ref(null);
	const theme = ref(null);
	const creatingError = ref(false);
	const creatingSuccess = ref(false);
	const isLoading = ref(true);
	const firstName = ref<string>(null);
	const lastName = ref<string>(null);
	const dateOfBirth = ref<Date>();
	const emailAddress = ref<string>(null);
	const phoneNumber = ref<string>(null);
	const pageInitialHeight = ref<number>(null);
	const pageInitialWidth = ref<number>(null);
	const storeCode = ref<string>(null);
	const termsList = ref<any[]>(null);
	const termsData = ref<LegalTerms[]>(null);
	const isPrivacyPolicyChecked = ref<boolean>(false);
	const isSMSConsentChecked = ref<boolean>(false);

	const firstNameLabel = computed<string>(() => (LOCALIZATION.creditApp.creationPage.firstName as any)[fimenu.language]);
	const lastNameLabel = computed<string>(() => (LOCALIZATION.creditApp.creationPage.lastName as any)[fimenu.language]);
	const emailAddressLabel = computed<string>(() => (LOCALIZATION.creditApp.creationPage.emailAddress as any)[fimenu.language]);
	const dateOfBirthLabel = computed<string>(() => (LOCALIZATION.creditApp.creationPage.dateOfBirth as any)[fimenu.language]);
	const phoneNumberLabel = computed<string>(() => (LOCALIZATION.creditApp.creationPage.phoneNumber as any)[fimenu.language]);

	const nameValidationMessage = computed<string>(() => (LOCALIZATION.creditApp.creationPage.nameValidationMessage as any)[fimenu.language]);
	const dateOfBirthFormatMessage = computed<string>(() => (LOCALIZATION.creditApp.creationPage.dateOfBirthFormatMessage as any)[fimenu.language]);
	const dateOfBirthUnderageMessage = computed<string>(() => (LOCALIZATION.creditApp.creationPage.dateOfBirthUnderageMessage as any)[fimenu.language]);
	const iAcknowledge = computed<string>(() => (LOCALIZATION.creditApp.creationPage.iAcknowledge as any)[fimenu.language]);
	const privacyPolicy = computed<string>(() => (LOCALIZATION.creditApp.creationPage.privacyPolicy as any)[fimenu.language]);
	const smsConsentButtonMessage = computed<string>(() => (LOCALIZATION.creditApp.creationPage.smsConsentButton as any)[fimenu.language]);
	const smsConsentText = computed<string>(() => (LOCALIZATION.creditApp.creationPage.smsConsentText as any)[fimenu.language]);
	const createApplicationMessage = computed<string>(() => (LOCALIZATION.creditApp.creationPage.createApplication as any)[fimenu.language]);

	onMounted(() => {
		const viewport = document.querySelector('meta[name=viewport]');
		viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0');
		pageInitialHeight.value = window.innerHeight;
		pageInitialWidth.value = window.innerWidth;
		if ($route.query?.prefilledData) {
			const decodedPrefilledData = JSON.parse(atob($route.query.prefilledData as string));
			firstName.value = decodedPrefilledData?.firstName;
			lastName.value = decodedPrefilledData?.lastName;
			emailAddress.value = decodedPrefilledData?.email;
			phoneNumber.value = decodedPrefilledData?.phone;
		}
		window.addEventListener('resize', onResizeHandler);

		// Load Foureyes script (cannot use <script> in Vue template - side effects are ignored)
		if (!document.querySelector('script[src*="foureyes.io/fe-init"]')) {
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.async = true;
			script.src = '//scripts.foureyes.io/fe-init.js';
			document.head.appendChild(script);
			// Remove after verifying: confirms Foureyes script was injected
			console.debug('Foureyes fe-init.js script injected');
		}
	});

	const localizationOpposite = computed(() => {
		if (fimenu.language == 'Spanish') {
			return 'English';
		} else {
			return 'Español';
		}
	});

	const hasAgreedToTerms = computed(() => {
		return termsList.value?.every(term => term.isChecked);
	});

	// If there is an error code, we display the error message. Else, return null;
	const errorMessage = computed(() => {
		if ($route.query?.errorCode) {
			return `(#${$route.query?.errorCode}). Please try accessing the page again from the original link. We apologize for any inconvenience and appreciate your understanding.`;
		}
		return null;
	});

	const requiredMessage = computed(() => {
		return LOCALIZATION.creditApp.creationPage.requiredMessage[fimenu.language as keyof typeof LOCALIZATION.creditApp.creationPage.requiredMessage];
	});

	onBeforeMount(async () => {
		fimenu.language = 'English';
		//If there is no store code, we redirect to the error page
		if (!$route.query.storeCode) {
			isLoading.value = false;
			util.logRocketTrack('No Store Code Customer App Creation', {
				url: window.location.href,
				environment: settings.environmentName,
			});
			return $router.push(`/customer-portal/no-access?errorCode=OTPPageNoStoreCode`);
		}
		if ($route.query.leadSource && $route.query.storeCode) {
			//GET STORE INFO (DETAILS)
			const response = await api.settings.getStoreBasicInfo($route.query.storeCode as string);
			if (response.data) {
				if (response.data.theme.mainTextColor) {
					if (response.data.storeFiles !== null && response.data.storeFiles.length > 0) {
						const { filepath, filetype } = response.data.storeFiles.find(f => f.filetype === StoreFileTypes.MainLogo);
						response.data.theme.logoSrc = filepath
							? `${settings.apiUrl}/Settings/GetStoreMainLogo?storeCode=${$route.query.storeCode}&ver=${Date.now()}`
							: null;
					}
				} else {
					response.data.theme = ApplicationsSettingsLoader.ApplicationSettings.theme;
				}

				fimenu.storeInfo = response.data;
				theme.value = fimenu.storeInfo.theme;
				storeCode.value = ($route.query.storeCode as string) ?? '';
			}

			isLoading.value = false;
		} else {
			isLoading.value = false;
		}

		localStorage.removeItem('applicationObject');
		localStorage.removeItem('fimenuObject');

		await getLegalTerms();
	});

	function languageChange() {
		if (fimenu.language == 'English') {
			fimenu.language = 'Spanish';
		} else {
			fimenu.language = 'English';
		}
		createTermsList();
	}

	function fillData() {
		firstName.value = 'Test';
		lastName.value = 'Testing';
		dateOfBirth.value = formatDateToDateObject('01/01/1991', { formatString: DEFAULT_DATE_FORMAT_DISPLAY_WITH_TIME });
		emailAddress.value = 'gretelalt01@gmail.com';
		phoneNumber.value = '3059798924';
	}

	function getSourceData() {
		const sourceData = {
			language: fimenu.language,
			firstName: firstName.value,
			lastName: lastName.value,
			dateOfBirth: dateOfBirth.value,
			phoneNumber: phoneNumber.value,
			emailAddress: emailAddress.value,
			storeCode: storeCode.value,
			storeName: fimenu.storeInfo.storeName,
			storePhone: fimenu.storeInfo.storePhone,
		};
		return sourceData;
	}

	function sendApplication() {
		createAppButtonRef.value.classList.add('clicked');
		setTimeout(() => {
			createAppButtonRef.value?.classList.remove('clicked');
		}, 400);

		if (v$.value.$invalid || !hasAgreedToTerms.value) {
			v$.value.$touch();
			return;
		}
		isLoading.value = true;
		const prefilledData = {
			language: fimenu.language,
			firstName: firstName.value,
			lastName: lastName.value,
			dateOfBirth: dateOfBirth.value,
			phoneNumber: phoneNumber.value,
			emailAddress: emailAddress.value,
			storeCode: storeCode.value,
		};
		// sourceData = prefilledData;

		const acceptedTermsDate = getTimeZoneDate(DEFAULT_TIMEZONE);

		api.applications
			.createApplication({
				isInStore: $route.query.isInStore,
				prefilledData,
				acceptedTerms: termsList.value.map(t => ({
					id: t.id,
					termsType: t.termsType,
					termsLabel: t.termsLabel,
					termsText: t.termsText,
				})),
				userLocalDateTime: acceptedTermsDate,
				createAppParams: $route.query,
			})
			.then(response => {
				// console.log(response)

				if (response.data) {
					customerApp.value = response.data.application;
					creatingSuccess.value = true;

					util.logRocketTrack('Customer_App_Creation', {
						storeCode: storeCode.value,
						email: emailAddress.value,
						environment: settings.environmentName,
					});

					util.logRocketTrack(`Lead_Created`, {
						leadId: response.data.leadId ?? '',
						customerCell: phoneNumber.value,
						customerEmail: emailAddress.value,
						customerDob: dateOfBirth.value?.toString(),
						storeName: fimenu.storeInfo.storeName,
						environment: settings.environmentName,
					});
				} else {
					creatingError.value = true;
				}
			})
			.catch(error => {
				creatingError.value = true;
				LogRocket.log('error creating customer', error);
			})
			.finally(() => {
				isLoading.value = false;
				if (creatingError.value) {
					util.logRocketTrack('Customer_App_Creation', {
						storeCode: storeCode.value,
						email: emailAddress.value,
						environment: settings.environmentName,
					});
				}
			});
	}

	function onResizeHandler() {
		const metaViewport = document.querySelector('meta[name=viewport]');

		if (window.innerHeight < pageInitialHeight.value) {
			// document.documentElement.style.setProperty('overflow', 'auto')
			metaViewport.setAttribute('content', 'height=' + pageInitialHeight.value + 'px,width=device-width,initial-scale=1.0');
		} else {
			metaViewport.setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=1.0,user-scalable=0');
		}
	}

	function getEnumDisplayValue(data: any, options: any) {
		const valueToMatch = options.find((term: any) => term.value === data);
		return valueToMatch.display;
	}

	function getRelevantTerm(termsType: any) {
		return termsList.value?.find(term => term.termsType === termsType);
	}

	async function getLegalTerms() {
		const getLatestVersion = true;

		const termsToFetch = [LEGAL_TERM_TYPES.PRIVACY_NOTICE, LEGAL_TERM_TYPES.SMS_CONSENT];

		try {
			const response = await api.settings.getLegalTermsByType(getLatestVersion, termsToFetch);
			termsData.value = response?.data;

			if (!termsData.value) {
				throw new Error('No legal terms data found');
			}
			createTermsList();
		} catch (Error) {
			console.error('Error fetching legal terms', Error);
			return;
		}
	}

	function createTermsList() {
		try {
			termsList.value = termsData.value.map(term => {
				const languageMatch = term.nameTranslations.find(nt => getEnumDisplayValue(nt.language, LANGUAGE_DISPLAY) === fimenu.language);

				if (!languageMatch) {
					throw new Error('No Language Match for Legal Terms. Please check if the legal terms have the correct language translations.');
				}

				return {
					id: term.id,
					dateCreated: term.dateCreated,
					termsType: term.termsType,
					termsText: languageMatch?.termsText,
					language: getEnumDisplayValue(languageMatch?.language, LANGUAGE_DISPLAY),
					termsLabel: languageMatch?.termsLabel,
					isChecked: false,
				};
			});
		} catch (error) {
			console.error('Error creating terms list', error);
		}
	}

	function handleTermsCheckbox(item: any) {
		if (item.termsType === LEGAL_TERM_TYPES.PRIVACY_NOTICE) {
			isPrivacyPolicyChecked.value = !isPrivacyPolicyChecked.value;
		} else if (item.termsType === LEGAL_TERM_TYPES.SMS_CONSENT) {
			isSMSConsentChecked.value = !isSMSConsentChecked.value;
		}
		termsList.value = termsList.value.map(term => {
			if (term.id === item.id) {
				return { ...term, isChecked: !term.isChecked };
			} else {
				return term;
			}
		});
	}

	function openPrivacyPolicyModal() {
		$modal.open(modalLegalTerm, {
			name: 'modalLegalTerm',
			passedData: {
				title: 'Privacy Policy',
				term: getRelevantTerm(LEGAL_TERM_TYPES.PRIVACY_NOTICE),
			},
			backdrop: true,
		});
	}

	function openSMSConsentModal() {
		$modal.open(modalLegalTerm, {
			name: 'modalLegalTerm',
			passedData: {
				title: 'SMS Consent',
				term: getRelevantTerm(LEGAL_TERM_TYPES.SMS_CONSENT),
			},
			backdrop: true,
		});
	}

	const validations = computed(() => ({
		firstName: { required, validName } as any,
		lastName: { required, validName } as any,
		dateOfBirth: {
			required: required,
			validDate: () => validDate(dateOfBirthRef.value?.dateDisplay),
			validDateOfBirth: () => validDateOfBirth(dateOfBirthRef.value?.dateDisplay),
			minLength: () => dateOfBirthRef.value?.dateDisplay?.length >= 10,
		} as any,
		emailAddress: { required, email } as any,
		phoneNumber: {
			required,
			numeric,
			minLength: minLength(10),
			maxLength: maxLength(10),
		} as any,
	}));

	const v$ = useVuelidate(validations.value, {
		firstName,
		lastName,
		dateOfBirth,
		emailAddress,
		phoneNumber,
	});
</script>
<style>
	.prefilled-page main,
	.prefilled-page main .saturn-textbox,
	.prefilled-page main .saturn-textbox input {
		background-color: var(--secondary-color);
	}

	.prefilled-page .autofill-btn {
		padding: 5px 10px;
		color: var(--swiper-theme-color);
		background: white;
		border: 1px solid var(--swiper-theme-color);
		border-radius: 5px;
	}

	.prefilled-page .autofill-btn:hover {
		background: var(--swiper-theme-color);
		color: white;
	}

	/*.form button {
        position: absolute;
        bottom: 15%;
        width: 80%;
        left: 50%;
        transform: translateX(-50%);
    }*/

	.prefilled-page .input-component {
		position: relative;
		display: inline-flex;
		flex-direction: column;
		align-items: center;
		min-width: 0;
		padding: 0;
		border: 0;
		vertical-align: top;
		width: 100%;
	}

	.prefilled-page .input-component select {
		border: none !important;
	}

	.prefilled-page .input-component .saturn-textbox:has(.saturn-icon) input {
		border-bottom-right-radius: unset;
		border-top-right-radius: unset;
		padding-right: 0px;
	}

	.input-component.invalid fieldset.floatingLabel,
	.prefilled-page .input-component.invalid .saturn-icon {
		border: 1px solid var(--error-color) !important;
		color: var(--error-color) !important;
	}

	.input-component.invalid label.label-root,
	.prefilled-page fieldset:disabled .input-component.invalid label.label-root {
		color: var(--error-color);
	}

	.prefilled-page .input-component.clearable input {
		padding: 10px 25px 10px 14px;
	}

	.prefilled-page .input-component.dropdown select {
		padding: 10px;
	}

	.prefilled-page fieldset:disabled .input-component button.clear-input {
		display: none;
	}

	.prefilled-page fieldset:disabled .input-component.dropdown > * {
		pointer-events: none;
	}

	.prefilled-page .input-component button.clear-input {
		border: none;
		position: absolute;
		right: 35px;
		width: unset;
		height: unset;
		color: unset;
		padding: 5px !important;
		background: none !important;
		box-shadow: none !important;
	}

	.prefilled-page .input-component button.clear-input.noIcon-padding {
		right: 5px;
	}

	.prefilled-page .input-component button.clear-input i {
		transition: all 0.2s ease-out;
	}

	.prefilled-page .input-component button.clear-input:hover i {
		color: var(--error-color);
	}

	.prefilled-page .input-component.invalid .saturn-icon {
		border-left: none !important;
	}

	.input-component.focused fieldset.floatingLabel,
	.prefilled-page .input-component.focused .saturn-icon {
		border: 1px solid var(--main-color) !important;
		color: var(--main-color) !important;
	}

	.prefilled-page .input-component.focused label.label-root {
		color: var(--main-color);
	}

	.input-component label.label-root,
	.prefilled-page fieldset:disabled .input-component label.label-root {
		color: rgba(0, 0, 0, 0.6);
		font-size: 1.2rem;
		line-height: 1.4375em;
		letter-spacing: 0.00938em;
		padding: 0;
		display: block;
		transform-origin: top left;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		max-width: 98%;
		position: absolute;
		left: 0;
		top: 0;
		z-index: 1;
		pointer-events: none;
		user-select: none;
		backdrop-filter: blur(4px);
		transform: translate(14px, -9px) scale(0.75);
		transition: all 200ms cubic-bezier(0, 0, 0.2, 1);
	}

	.prefilled-page .input-component.left-icon label.label-root {
		left: 36px;
		transform: translate(6px, -9px) scale(0.75);
		max-width: calc(98% - 55px);
	}

	fieldset:disabled .input-component.left-icon label.label-root,
	.prefilled-page fieldset:disabled .input-component.left-icon.empty:not(.focused) label.label-root {
		left: 0;
		transform: translate(13px, -9px) scale(0.75);
		max-width: calc(98% - 35px);
	}

	.prefilled-page .input-component.empty:not(.focused) label.label-root {
		max-width: 65%;
		top: 50%;
		transform: translate(12px, -50%) scale(0.95);
		z-index: 1;
		backdrop-filter: blur(2px);
	}

	.prefilled-page .input-component.left-icon.empty:not(.focused) label.label-root {
		transform: translate(5px, -50%) scale(0.95);
		max-width: calc(100% - 45px);
	}

	.prefilled-page fieldset:disabled .input-component.empty:not(.focused) label.label-root {
		max-width: 98%;
		width: unset;
		top: 0;
		transform: translate(14px, -9px) scale(0.75);
		z-index: 2;
	}

	fieldset:disabled .input-component label.label-root,
	.input-component:has(input:disabled) label.label-root,
	.input-component:has(textarea:disabled) label.label-root,
	.prefilled-page .input-component:has(select:disabled) label.label-root {
		font-weight: 800;
	}

	.prefilled-page .input-component .saturn-textbox {
		font-size: 1rem;
		line-height: 1.4375em;
		letter-spacing: 0.00938em;
		color: rgba(0, 0, 0, 0.87);
		box-sizing: border-box;
		cursor: text;
		display: inline-flex;
		-webkit-box-align: center;
		align-items: center;
		position: relative;
		border-radius: 4px;
		/*height: calc(1.475em + 30px); */
		height: calc(1.8rem + 30px);
		width: 100%;
		max-height: 100%;
		/*max-height: 40px;*/
		/*z-index: 1;
        overflow: hidden;*/
	}

	.prefilled-page .input-component .saturn-textbox fieldset.floatingLabel {
		position: absolute;
		box-sizing: border-box;
		border: 1px solid rgba(0, 0, 0, 0.23) !important;
		border-radius: 4px;
		top: -5px;
		bottom: 0;
		left: 0;
		width: 100%;
		padding: 0 8px;
		margin: 0;
		height: unset !important;
		pointer-events: none;
		transition: all 200ms cubic-bezier(0, 0, 0.2, 1);
	}

	.prefilled-page .input-component.left-icon .saturn-textbox fieldset.floatingLabel {
		padding: 0 10% 0 36px;
	}

	.prefilled-page fieldset:disabled .input-component.left-icon .saturn-textbox fieldset.floatingLabel {
		padding: 0 0 0 8px;
	}

	.prefilled-page .input-component .saturn-textbox fieldset.floatingLabel legend.label-with-infoicon-containter {
		float: unset;
		width: auto;
		overflow: hidden;
		display: block;
		padding: 0;
		height: 11px;
		font-size: 0.75em;
		visibility: hidden;
		transition: max-width 50ms cubic-bezier(0, 0, 0.2, 1) 0ms;
		max-width: 85%;
		text-align: left;
	}

	.prefilled-page .input-component.empty .saturn-textbox fieldset.floatingLabel legend.label-with-infoicon-containter {
		max-width: 0.01px;
	}

	.prefilled-page fieldset:disabled .input-component.empty .saturn-textbox fieldset.floatingLabel legend.label-with-infoicon-containter {
		max-width: 85%;
	}

	.prefilled-page .input-component .saturn-textbox fieldset.floatingLabel legend.label-with-infoicon-containter span {
		padding-left: 5px;
		padding-right: 5px;
		display: inline-block;
		opacity: 0;
		visibility: visible;
	}

	.prefilled-page .input-component .saturn-textbox .saturn-icon {
		box-sizing: border-box;
		height: 100%;
		min-width: 34px;
		display: flex;
		justify-content: center;
		align-items: center;
		background-color: var(--input-component-saturn-icon-background-color);
		color: var(--input-component-background-color);
		font-size: 0.9rem;
		cursor: default;
		border: none !important;
		transition: all 200ms cubic-bezier(0, 0, 0.2, 1);
	}

	.prefilled-page .input-component.left-icon .saturn-textbox .saturn-icon {
		border-top-left-radius: 4px;
		border-bottom-left-radius: 4px;
	}

	.prefilled-page .input-component.left-icon .saturn-textbox .action-slot {
		right: unset;
		left: 3px;
	}

	.prefilled-page .input-component.action.left-icon .saturn-textbox:has(.action-slot) input {
		padding-left: 42px;
		border-top-left-radius: 4px;
		border-bottom-left-radius: 4px;
	}

	.input-component.left-icon .saturn-textbox input,
	.prefilled-page .input-component.left-icon .saturn-textbox select {
		padding: 10px 5px;
		border-top-left-radius: unset;
		border-bottom-left-radius: unset;
		border-top-right-radius: 4px !important;
		border-bottom-right-radius: 4px !important;
	}

	.InputCurrency .input-component.left-icon .saturn-textbox input,
	.prefilled-page .InputNumber .input-component.left-icon .saturn-textbox input {
		text-align: end;
	}

	.prefilled-page .input-component:not(.left-icon) .saturn-textbox .saturn-icon {
		border-top-right-radius: 4px;
		border-bottom-right-radius: 4px;
	}

	.prefilled-page .input-component.dropdown .saturn-textbox .saturn-icon {
		position: absolute;
		right: 0;
		top: 0;
		pointer-events: none;
	}

	.prefilled-page .input-component.dropdown .saturn-textbox .saturn-icon {
		background: unset !important;
		border: unset !important;
	}

	.prefilled-page .input-component.dropdown .saturn-textbox .saturn-icon i {
		margin-top: -4px;
		color: var(--text-color);
	}

	.prefilled-page .input-component.dropdown.invalid .saturn-textbox .saturn-icon i {
		margin-top: -4px;
		color: var(--error-color);
	}

	.prefilled-page .input-component.dropdown.focused .saturn-textbox .saturn-icon i {
		color: var(--main-color);
	}

	.prefilled-page .input-component.dropdown.focused .saturn-textbox .saturn-icon {
		transform: rotate(180deg);
	}

	.prefilled-page .input-component .saturn-textbox-error-message {
		display: none;
	}

	.prefilled-page .textbox-error-message {
		display: block;
		top: 100%;
		left: 2px;
		background-color: var(--secondary-color);
		color: var(--error-color);
		font-size: 0.85rem;
		animation: expandable 0.1s ease-out forwards;
		z-index: 1000;
		align-self: flex-start;
	}
	.prefilled-page .textbox-error-message .error-emphasis {
		font-size: 0.85rem;
		font-style: italic;
	}

	.prefilled-page .input-component .defaultIconClass {
		color: var(--main-color);
		position: absolute;
		top: -6px;
		right: -6px;
		z-index: 999;
	}

	.prefilled-page .input-component .saturn-textbox textarea {
		font-family: Roboto, sans-serif;
		font-size: 12pt;
		padding: 15px;
		box-sizing: border-box;
		resize: none;
		border: none;
		height: 100%;
		width: 100%;
	}

	.prefilled-page .input-component .saturn-textbox:has(textarea) {
		aspect-ratio: 1 / 0.75;
	}

	.prefilled-page .input-component.multi-line.empty:not(.focused) label.label-root {
		top: 15px;
		transform: translate(12px, 0) scale(0.95);
	}

	.prefilled-page .input-component.action input {
		padding-right: 40px;
	}

	.prefilled-page .action-slot {
		position: absolute;
		right: 2px;
		top: 50%;
		transform: translateY(-50%);
	}

	.prefilled-page .action-slot button {
		height: 35px;
		width: 35px;
		padding: unset;
		border-radius: 4px;
		position: relative;
	}

	.prefilled-page .action-slot button i {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}

	.prefilled-page .input-component .skeleton.match input {
		border-left: 0;
	}

	.prefilled-page .input-component .skeleton .cdk .cdk-textbox {
		width: 100%;
	}

	.prefilled-page .input-component .skeleton.icon-on-left .cdk .cdk-icon {
		right: unset;
		/*
            left: 7px;*/
	}

	/* InputCheckbox
       ----------------------------- */

	.prefilled-page .input-checkbox {
		display: flex;
		align-items: center;
		padding: 5px 0px;
		cursor: pointer;
	}

	.prefilled-page .input-checkbox .ic-label {
		margin-left: 10px;
	}

	.prefilled-page .input-checkbox .disabled {
		color: var(--disabled-color);
		cursor: not-allowed;
	}

	/* InputRadio
       ----------------------------- */

	.prefilled-page .radio-button-inline .gap {
		gap: 5px;
	}

	/* InputDate
       ----------------------------- */

	.prefilled-page .vdpPeriodControl button {
		color: var(--input-datepicker-button-color-text);
		font-weight: bold;
		height: auto;
	}

	.prefilled-page .vdpCell.selectable.today {
		color: var(--error-color);
		font-weight: 500;
		padding: 0 !important;
		border: 3px solid var(--error-color) !important;
		border-radius: 50%;
	}

	.prefilled-page .vdpTable thead {
		background: none !important;
	}

	.prefilled-page .vdpTable thead th {
		padding: 0.3em 0.5em 1.8em !important;
		border: none !important;
	}

	.prefilled-page .vdpTable tbody tr {
		background: none !important;
	}

	.prefilled-page .vdpTable td {
		padding: 0.5em 0 !important;
		border: none !important;
	}

	.prefilled-page fieldset {
		border: none;
	}

	.prefilled-page .form {
		margin-top: 12px;
		row-gap: 20px;
	}

	.prefilled-page .terms-checkbox-button.invalid i {
		color: var(--error-color);
	}

	.prefilled-page .clicked {
		background-color: rgb(33 138 11) !important;
	}
</style>
